/**
 * Product-facing error copy for the Matrix IDP console.
 * Never surface raw API codes (e.g. INVALID_STATE_TRANSITION) as primary UI chrome.
 */

import { OpsApiError } from "./api.ts";

const API_ERROR_TITLES: Record<string, string> = {
  INVALID_STATE_TRANSITION: "当前状态不允许此操作",
  INVALID_STATE: "状态不合法",
  CONFLICT: "操作冲突",
  PRECONDITION_FAILED: "前置条件未满足",
  VALIDATION_ERROR: "请求未通过校验",
  BAD_REQUEST: "请求无效",
  NOT_FOUND: "未找到资源",
  KNOWLEDGE_NOT_FOUND: "未找到知识资源",
  KNOWLEDGE_REPOSITORY_NOT_FOUND: "未找到知识库数据",
  FORBIDDEN: "没有权限",
  UNAUTHORIZED: "未授权",
  HTTP_ERROR: "请求失败",
  INTERNAL_ERROR: "服务内部错误",
  SERVICE_UNAVAILABLE: "服务暂时不可用",
  TIMEOUT: "请求超时",
  RATE_LIMITED: "请求过于频繁"
};

const MESSAGE_REPLACEMENTS: Array<{ pattern: RegExp; label: string }> = [
  {
    pattern: /base\s+Graph\s+Snapshot\s+is\s+incomplete/i,
    label: "基础概念图谱尚未构建完整，请先完成概念结构相关阶段后再操作"
  },
  {
    pattern: /Graph\s+Snapshot\s+is\s+incomplete/i,
    label: "概念图谱尚未构建完整"
  },
  {
    pattern: /Graph\s+root\s+set\s+is\s+incomplete\s+or\s+invalid/i,
    label: "概念图谱顶层结构不完整或无效"
  },
  {
    pattern: /Graph\s+Snapshot\s+Forest\s+does\s+not\s+cover\s+every\s+Concept/i,
    label: "概念森林未覆盖全部概念"
  },
  {
    pattern: /Impact\s+Plan\s+does\s+not\s+belong\s+to\s+the\s+supplied\s+base\s+Graph/i,
    label: "影响计划与当前基础图谱不匹配"
  },
  {
    pattern: /task\s+(is\s+)?already\s+(completed|finished|succeeded)/i,
    label: "任务已经结束，无法再执行该操作"
  },
  {
    pattern: /任务已经完成/,
    label: "任务已经结束，无法再执行该操作"
  },
  {
    pattern: /expected\s+revision/i,
    label: "数据已被他人更新，请刷新后重试"
  },
  {
    pattern: /revision\s+mismatch/i,
    label: "版本修订号不一致，请刷新后重试"
  },
  {
    pattern: /not\s+sealed/i,
    label: "版本尚未定版，无法标记为可用"
  },
  {
    pattern: /already\s+available/i,
    label: "版本已是可用状态"
  },
  {
    pattern: /already\s+stable/i,
    label: "该版本已是稳定版"
  }
];

export type ProductErrorView = {
  title: string;
  message: string;
  requestId?: string;
  /** Optional collapsed tech detail for support / clipboard. */
  technical?: string;
};

export function productizeError(error: unknown): ProductErrorView {
  if (error instanceof OpsApiError) {
    const code = error.details.code;
    const title = API_ERROR_TITLES[code] ?? "操作未完成";
    const message = productizeMessage(error.message) || defaultMessageForCode(code);
    const technicalParts = [code, error.message].filter(Boolean);
    if (error.details.details && Object.keys(error.details.details).length > 0) {
      technicalParts.push(JSON.stringify(error.details.details));
    }
    return {
      title,
      message,
      ...(error.details.requestId ? { requestId: error.details.requestId } : {}),
      technical: technicalParts.join(" · ")
    };
  }
  if (error instanceof Error) {
    return {
      title: "操作未完成",
      message: productizeMessage(error.message) || "发生未知错误，请稍后重试",
      technical: error.message
    };
  }
  return {
    title: "操作未完成",
    message: "发生未知错误，请稍后重试",
    technical: String(error)
  };
}

export function productizeMessage(message: string | null | undefined): string {
  if (!message?.trim()) return "";
  const trimmed = message.trim();
  for (const { pattern, label } of MESSAGE_REPLACEMENTS) {
    if (pattern.test(trimmed)) return label;
  }
  if (/^[A-Z][A-Z0-9_]+$/.test(trimmed)) {
    return humanizeToken(trimmed);
  }
  if (/[A-Za-z]{4,}/.test(trimmed) && !/[\u4e00-\u9fff]/.test(trimmed)) {
    return softenEnglishDiagnostic(trimmed);
  }
  return trimmed;
}

function defaultMessageForCode(code: string): string {
  switch (code) {
    case "INVALID_STATE_TRANSITION":
      return "当前版本或任务状态不支持该操作，请刷新页面后确认构建进度。";
    case "CONFLICT":
    case "PRECONDITION_FAILED":
      return "数据状态已变化，请刷新后重试。";
    case "NOT_FOUND":
    case "KNOWLEDGE_NOT_FOUND":
      return "目标资源不存在或已被删除。";
    default:
      return "请稍后重试；若持续失败，可联系支持并提供请求编号。";
  }
}

function softenEnglishDiagnostic(message: string): string {
  const text = message
    .replace(/\bGraph\s+Snapshot\b/gi, "概念图谱")
    .replace(/\bbase\s+Graph\b/gi, "基础概念图谱")
    .replace(/\bImpact\s+Plan\b/gi, "影响计划")
    .replace(/\bConcept\s+Forest\b/gi, "概念森林")
    .replace(/\bincomplete\b/gi, "不完整")
    .replace(/\binvalid\b/gi, "无效")
    .replace(/\bmust\s+not\b/gi, "不能")
    .replace(/\bcannot\b/gi, "无法")
    .replace(/\bfailed\b/gi, "失败");
  if (/^[A-Za-z]/.test(text) && text.length > 12) {
    return `构建条件未满足：${text}`;
  }
  return text;
}

export function humanizeToken(token: string): string {
  if (API_ERROR_TITLES[token]) return API_ERROR_TITLES[token];
  return token
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}
